import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Zap, Home, Building, CheckCircle } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import owc from './images1/OWC.png';
import bioguest from './images1/bioguest.png';
import hrms from './images1/24hrs.png';
import smsh from './images1/smsh.png';
import none from './images1/nonele.png';

const comparisonData = [
  {
    parameter: 'Process Type',
    bioguest: 'In-vessel aerobic, natural',
    automatic: 'Thermally accelerated',
    semiAuto: 'Hybrid (machine + yard)',
    home: 'Natural aerobic'
  },
  {
    parameter: 'Heating Element',
    bioguest: 'Not required',
    automatic: 'Required',
    semiAuto: 'Not required',
    home: 'Not required'
  },
  {
    parameter: 'Energy Consumption',
    bioguest: 'Very low',
    automatic: 'High',
    semiAuto: 'Low',
    home: 'Nil'
  },
  {
    parameter: 'Processing Time',
    bioguest: 'Continuous, biological',
    automatic: '~24 hours',
    semiAuto: '3-4 weeks',
    home: 'Gradual'
  },
  {
    parameter: 'Odor Control',
    bioguest: 'Excellent (in-vessel)',
    automatic: 'Moderate',
    semiAuto: 'Depends on management',
    home: 'Excellent'
  },
  {
    parameter: 'Compost Quality',
    bioguest: 'High, stabilized',
    automatic: 'Partially stabilized',
    semiAuto: 'High',
    home: 'High'
  },
  {
    parameter: 'Space Requirement',
    bioguest: 'Low',
    automatic: 'Low',
    semiAuto: 'High (yard needed)',
    home: 'Very low'
  },
  {
    parameter: 'Environmental Impact',
    bioguest: 'Highly sustainable',
    automatic: 'Energy intensive',
    semiAuto: 'Sustainable',
    home: 'Highly sustainable'
  },
  {
    parameter: 'Typical Applications',
    bioguest: 'Institutions, hotels, campuses',
    automatic: 'Large commercial facilities',
    semiAuto: 'Societies, institutions',
    home: 'Individual homes'
  },
  {
    parameter: 'Compliance (SWM 2016)',
    bioguest: 'Yes',
    automatic: 'Conditional',
    semiAuto: 'Yes',
    home: 'Yes'
  }
];

const systems = [
  {
    title: 'BIOGUEST-V',
    subtitle: 'Fully Automatic Natural In-Vessel Composting',
    icon: Building,
    features: [
      'No thermal heaters required',
      'Integrated curing mechanism',
      'Very low power consumption',
      'Odor-free operation',
      'Capacity: 25 kg to 2000 kg/day'
    ],
    description: 'Relies on optimized microbial kinetics, controlled aeration, and internal curing to convert organic waste into compost with minimal energy input.'
  },
  {
    title: '24-Hour Automatic Converter',
    subtitle: 'Thermal Accelerated System',
    icon: Zap,
    features: [
      'Continuous automated operation',
      'High throughput capacity',
      'Rapid processing',
      'Capacity: 100 kg to 5000 kg/day',
      'Accelerated moisture reduction'
    ],
    description: 'Mechanized, thermally accelerated waste processing unit designed to rapidly convert organic waste within a short cycle time.'
  },
  {
    title: 'Semi-Automatic System',
    subtitle: 'Hybrid Composting Solution',
    icon: Leaf,
    features: [
      'Initial mechanical mixing (~2 hours)',
      'Secondary aerobic curing (3-4 weeks)',
      'Lower energy costs',
      'Capacity: 100 kg to 5000 kg/day',
      'Produces stable compost'
    ],
    description: 'Combines short-duration mechanical initiation with yard-based aerobic maturation for optimal composting results.'
  },
  {
    title: 'Home Composting',
    subtitle: 'Non-Electric Solution',
    icon: Home,
    features: [
      'Zero electricity consumption',
      'Compact and aesthetic design',
      'Daily waste loading',
      'Low maintenance',
      'Perfect for households'
    ],
    description: 'Non-electric, compact, in-vessel composting solution intended for households and small establishments.'
  }
];

export const CompostingPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="composting-page">
      {/* Hero Section */}
      <section className="bg-emerald-950 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-amber-400 uppercase tracking-[0.3em] text-sm font-semibold">
              Organic Waste Solutions
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Composting-Based Treatment Systems
            </h1> 
            <p className="text-white/70 text-lg max-w-3xl mx-auto mb-8 tracking-wide leading-relaxed">
              Engineered systems for controlled biological degradation of biodegradable solid waste, 
              converting organic matter into nutrient-rich compost.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Organic Waste Composting Systems (OWC) */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
                1. Organic Waste Composting Systems (OWC)
              </h2>
              <p className="text-slate-600 dark:text-white/70 mb-6 tracking-wider leading-relaxed">
                An Organic Waste Composter (OWC) is an engineered system designed for the controlled biological degradation of biodegradable solid waste such as food waste, horticultural residues, paper-based organics, and other putrescible materials. Through aerobic microbial stabilization, organic matter is converted into nutrient-rich compost suitable for soil conditioning and landscaping applications.
              </p>
              <p className="text-slate-600 dark:text-white/70 mb-6 tracking-wider leading-relaxed">OWC systems provide a decentralized and sustainable solution for organic waste management by significantly reducing waste sent to landfills, minimizing transportation requirements, and supporting circular resource recovery. Multiple system configurations are available to suit site specific waste quantities, operational constraints, and compliance requirements.</p>
              
              <h3 className="text-xl font-semibold text-emerald-900 dark:text-white mb-4">
                Technical & Environmental Advantages
              </h3>
              <ul className="space-y-3">
                {[
                  'Automated and controlled aerobic degradation',
                  'Lower greenhouse gas emissions',
                  'Alignment with ESG objectives',
                  'Compliance with Solid Waste Management Rules, 2016',
                  'Production of stabilized compost',
                  'Reduced odor, leachate, and pest attraction'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 dark:text-white/70">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={owc}
                alt="Composting System"
                className="w-full h-auto rounded-lg shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Fully Automatic Natural In-Vessel Composting System (BIOGUEST-V) */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={bioguest}
                alt="Composting System"
                className="w-full h-auto rounded-lg shadow-xl"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
                2. Fully Automatic Natural In-Vessel Composting System (BIOGUEST-V)
              </h2>
              <p className="text-slate-600 dark:text-white/70 mb-6 tracking-wider leading-relaxed">
                The BIOGUEST is a fully automatic, in-vessel composting reactor designed for natural aerobic degradation without the use of external heating elements. The system relies on optimized microbial kinetics, controlled aeration, and internal curing to convert organic waste into compost with minimal energy input.
              </p>
              
              <h3 className="text-xl font-semibold text-emerald-900 dark:text-white mb-4">
                Technical Highlights
              </h3>
              <ul className="space-y-3">
                {[
                  'No thermal heaters; composting occurs under controlled biological conditions',
                  'Integrated curing mechanism for continuous compost withdrawal',
                  'Alignment with ESG objectives',
                  'Very low operating power consumption',
                  'Odor-free operation under proper waste segregation',
                  'Suitable for institutional, commercial, and residential applications'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 dark:text-white/70">{item}</span>
                  </li>
                ))}
              </ul><br></br>
              <p className="text-slate-600 dark:text-white/70 mb-6 tracking-wider leading-relaxed"><b>Waste Types Handled:</b>Food waste, vegetable and fruit waste, garden residues, paper waste, cardboard, pizza boxes <br></br>
              <b>Capacity Range:</b>25 kg/day to 2000 kg/day (site-specific models)<br></br>
              <b>Operational TrackRecord:</b>Operational installations across India with long-term field performance
              </p>
              
            </motion.div>

          </div>
        </div>
      </section>
      {/* 24-Hour Automatic Waste Converter (Thermal Accelerated System) */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
                3. 24-Hour Automatic Waste Converter (Thermal Accelerated System)
              </h2>
              <p className="text-slate-600 dark:text-white/70 mb-6 tracking-wider leading-relaxed">
                The 24-hour Automatic Waste Converter is a mechanized, thermally accelerated waste processing unit designed to rapidly convert organic waste into a soil amendment within a short cycle time using controlled heating and mechanical mixing.
              </p>
              <ul className="space-y-3">
                {[
                  'Continuous automated operation',
                  'High throughput capacity',
                  'Accelerated moisture reduction and partial stabilization'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 dark:text-white/70">{item}</span>
                  </li>
                ))}
              </ul>
              <h3 className="text-xl font-semibold text-emerald-900 dark:text-white mb-4">
                Capacity Range: 
              </h3>
              <p className="text-slate-600 dark:text-white/70 mb-6 tracking-wider leading-relaxed">100 kg/day to 5000 kg/day</p>
              <h3 className="text-xl font-semibold text-emerald-900 dark:text-white mb-4">
                Technical Limitations 
              </h3>
              <ul className="space-y-3">
                {[
                  'High electrical energy consumption',
                  'Higher capital and operating costs',
                  'Heat-driven processes may result in indirect GHG emissions',
                  'Output requires additional stabilization before land application'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 dark:text-white/70">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={hrms}
                alt="Composting System"
                className="w-full h-auto rounded-lg shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>
      {/* Semi-Automatic Natural Composting System */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={smsh}
                alt="Composting System"
                className="w-full h-auto rounded-lg shadow-xl"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
                4. Semi-Automatic Natural Composting System
              </h2>
              <p className="text-slate-600 dark:text-white/70 mb-6 tracking-wider leading-relaxed">
                The Semi-Automatic Composting System combines short-duration mechanical initiation with yard-based aerobic maturation. Initial homogenization and inoculation occur inside the machine, after which the material undergoes natural composting in curing beds.
              </p>
              <h3 className="text-xl font-semibold text-emerald-900 dark:text-white mb-4">
                Process Description 
              </h3>
              <ul className="space-y-3">
                {[
                  'Initial mechanical mixing with microbial inoculum (~2 hours)',
                  'Secondary aerobic curing in open or covered yard for 3–4 weeks',
                  'Produces stable, nutrient-rich compost'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 dark:text-white/70">{item}</span>
                  </li>
                ))}
              </ul>
              <h3 className="text-xl font-semibold text-emerald-900 dark:text-white mb-4">
                Capacity Range: 
              </h3>
              <p className="text-slate-600 dark:text-white/70 mb-6 tracking-wider leading-relaxed">100 kg/day to 5000 kg/day</p>
              <h3 className="text-xl font-semibold text-emerald-900 dark:text-white mb-4">
                Key Considerations
              </h3>
              <ul className="space-y-3">
                {[
                  'Requires adequate open space',
                  'Longer compost stabilization period',
                  'Lower energy and capital costs compared to fully automatic systems'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 dark:text-white/70">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

          </div>
        </div>
      </section>
      {/* Non-Electric Home Composting System */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
                5. Non-Electric Home Composting System
              </h2>
              <p className="text-slate-600 dark:text-white/70 mb-6 tracking-wider leading-relaxed">
                The home Composting Unit is a non-electric, compact, in-vessel composting solution intended for households and small establishments. The system operates entirely on natural aerobic biological processes with odor control through proper microbial balance.
              </p>
              <h3 className="text-xl font-semibold text-emerald-900 dark:text-white mb-4">
                Features 
              </h3>
              <ul className="space-y-3">
                {[
                  'Zero electricity consumption',
                  'Compact and aesthetic design',
                  'Daily waste loading and compost harvesting',
                  'Low maintenance and operating cost',
                  'Suitable for decentralized household waste management'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-600 dark:text-white/70">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={none}
                alt="Composting System"
                className="w-full h-auto rounded-lg shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>
      {/* Systems Grid */}
      <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-4">
              Our Composting Solutions
            </h2>
            <p className="text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">
              Multiple system configurations to suit site-specific requirements
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {systems.map((system, index) => (
              <motion.div
                key={system.title}
                className="bg-white dark:bg-emerald-900 p-8 shadow-sm hover:shadow-lg transition-shadow"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-800 rounded-full flex items-center justify-center">
                    <system.icon className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-emerald-900 dark:text-white">
                      {system.title}
                    </h3>
                    <p className="text-sm text-amber-500 tra">{system.subtitle}</p>
                  </div>
                </div>
                <p className="text-slate-600 dark:text-white/70 mb-4 text-sm tracking-wider leading-relaxed">
                  {system.description}
                </p>
                <ul className="space-y-2">
                  {system.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <span className="text-emerald-500 mt-1">•</span>
                      <span className="text-slate-600 dark:text-white/70">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-4">
              Comparative Technical Table
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="overflow-x-auto"
          >
            <div className="bg-white dark:bg-emerald-900 rounded-lg shadow-lg overflow-hidden min-w-[900px]">
              <Table>
                <TableHeader>
                  <TableRow className="bg-emerald-900">
                    <TableHead className="text-white">Parameter</TableHead>
                    <TableHead className="text-amber-400">BIOGUEST-V</TableHead>
                    <TableHead className="text-white">24-Hr Converter</TableHead>
                    <TableHead className="text-white">Semi-Automatic</TableHead>
                    <TableHead className="text-white">Home Composting</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow key={index} className={index % 2 === 0 ? 'bg-emerald-50/50 dark:bg-emerald-950/50' : ''}>
                      <TableCell className="font-medium">{row.parameter}</TableCell>
                      <TableCell className="text-emerald-600 dark:text-emerald-400 font-medium">{row.bioguest}</TableCell>
                      <TableCell>{row.automatic}</TableCell>
                      <TableCell>{row.semiAuto}</TableCell>
                      <TableCell>{row.home}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* NAAC Compliance */}
      <section className="py-12 md:py-10 bg-emerald-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              NAAC Green Audit Compliance
            </h2>
            <p className="text-white/70 text-lg mb-8 tracking-wide leading-relaxed">
              Our composting systems support NAAC green audit compliance through renewable energy adoption, 
              waste reduction, and measurable sustainability indicators. By integrating composting plants, 
              institutions can significantly enhance their energy efficiency, carbon footprint management, 
              and environmental stewardship.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-10 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-emerald-950 mb-4">
            Ready to Implement Composting Solutions?
          </h2>
          <p className="text-emerald-900 text-lg mb-8 tracking-wide leading-relaxed">
            Contact us to discuss the right composting system for your requirements.
          </p>
          <Link to="/contact">
            <motion.button
              className="bg-emerald-900 hover:bg-emerald-800 text-white px-10 py-4 uppercase tracking-wider font-semibold inline-flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="composting-cta-btn"
            >
              Get in Touch
              <ArrowRight size={20} />
            </motion.button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CompostingPage;
